import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, UsersRound } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { cn } from "@/lib/utils";

export function TeamSelector() {
  const teams = useAuthStore((state) => state.teams);
  const activeTeam = useAuthStore((state) => state.activeTeam);
  const switchTeam = useAuthStore((state) => state.switchTeam);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  async function choose(teamId: string) {
    if (teamId === activeTeam?.id) return setOpen(false);
    setSwitching(teamId);
    setError(null);
    try {
      await switchTeam(teamId);
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Team could not be switched.");
    } finally {
      setSwitching(null);
    }
  }

  if (!activeTeam) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((value) => !value)}
        className={cn(
          "flex min-h-11 w-full items-center gap-2 rounded-lg border border-studio-border bg-studio-panel px-3 py-2 text-left text-sm transition-[border-color,box-shadow,transform] hover:border-studio-border-light active:scale-[0.96]",
          open && "border-studio-border-light shadow-sm"
        )}
      >
        <UsersRound className="h-4 w-4 shrink-0 text-studio-accent" />
        <span className="min-w-0 flex-1"><span className="block truncate font-medium text-studio-ink">{activeTeam.name}</span><span className="block truncate text-[10px] capitalize text-studio-faint">{activeTeam.role.replace(/_/g, " ")}</span></span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-studio-faint" />
      </button>
      {open && (
        <div role="listbox" className="absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-xl border border-studio-border-light bg-studio-panel p-1.5 shadow-panel animate-rise">
          {teams.map((team) => (
            <button
              key={team.id}
              type="button"
              role="option"
              aria-selected={team.id === activeTeam.id}
              disabled={Boolean(switching)}
              onClick={() => void choose(team.id)}
              className={cn("flex min-h-11 w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-[background-color,color,transform] active:scale-[0.96]", team.id === activeTeam.id ? "bg-studio-card text-studio-ink" : "text-studio-muted hover:bg-studio-card/60 hover:text-studio-ink")}
            >
              {switching === team.id ? <Loader2 className="h-4 w-4 animate-spin text-studio-accent" /> : <UsersRound className="h-4 w-4 text-studio-accent" />}
              <span className="min-w-0 flex-1"><span className="block truncate">{team.name}</span><span className="block text-[10px] capitalize text-studio-faint">{team.role.replace(/_/g, " ")}</span></span>
              {team.id === activeTeam.id && <Check className="h-3.5 w-3.5 text-studio-accent" />}
            </button>
          ))}
          {error && <p role="alert" className="mt-1 rounded-lg bg-studio-danger/10 px-2.5 py-2 text-xs text-studio-danger">{error}</p>}
        </div>
      )}
    </div>
  );
}
